import React, { FC, Fragment } from 'react';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { connect, FormikProps, FieldArrayRenderProps } from 'formik';
import { Card, CardContent, Divider, Grid, IconButton, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import { ContentInput } from 'api';
import { UserGroup } from 'types/app';
import { Body } from 'features/content/widgets';
import { ValidatedSwitch } from 'components';

import { AdminCustomization, PostButton } from './EditorPartials';
import Preview from './Preview';

interface IProps {
  working: boolean;
  groups: UserGroup[];
  onClose: () => void;
  onGroupToggle: (group: UserGroup, arrayHelper: FieldArrayRenderProps) => void;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<ContentInput>;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(3),
    },
    headerText: {
      paddingLeft: theme.spacing(2),
    },
    contentActions: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: 0,
    },
    contentOptions: {
      paddingBottom: 0,
    },
    dividerActions: {
      marginTop: theme.spacing(1),
    },
    dividerButton: {
      marginBottom: theme.spacing(2),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

const ManualEditor: FC<IPropsEnhanced> = props => {
  const { t } = useTranslation();
  const { classes, formik, working, onClose } = props;
  const { isValid } = formik;

  function renderOptions() {
    return (
      <Grid container alignItems="center" justify="flex-end">
        <Grid item>
          <ValidatedSwitch name="is_shareable" color="primary" label={t('posting:shareable')} />
        </Grid>
      </Grid>
    );
  }

  function renderAdminCustomization() {
    return (
      <Fragment>
        <Divider />
        <CardContent className={classes.contentOptions}>
          <AdminCustomization allowUtm />
        </CardContent>
      </Fragment>
    );
  }

  function renderHeader() {
    return (
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Typography className={classes.headerText} variant="subtitle2">
            {t('content:postEdit')}
          </Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={onClose}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <Card className={classes.root}>
      {renderHeader()}
      <CardContent className={classes.contentActions}>
        <Body />
        <Preview content={formik.values} />
      </CardContent>
      <Divider className={classes.dividerActions} />
      <CardContent className={classes.contentOptions}>{renderOptions()}</CardContent>
      {renderAdminCustomization()}
      <Divider className={classes.dividerButton} />
      <CardContent>
        <PostButton i18nKey="common:save" working={working} disabled={!isValid} />
      </CardContent>
    </Card>
  );
};

export default enhancers(ManualEditor);
